import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ClickAwayListener } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CriteriaTemplate } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { MiSpinner } from '../../components/wizard-ui';

interface Props {
  name: string;
  modified: boolean;
  selectedId: string;
  templates: CriteriaTemplate[] | null;
  error: boolean;
  busy: boolean;
  onPick: (template: CriteriaTemplate) => void;
}

/** Vorlagen-Auswahl: Feld mit Name/Badge und aufklappbarem Menü (Design: `qOpen`). */
const TemplateDropdown: React.FC<Props> = ({
  name,
  modified,
  selectedId,
  templates,
  error,
  busy,
  onPick,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classes.wrap}>
        <button
          type="button"
          className={classes.field}
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <BookmarksOutlinedIcon sx={{ fontSize: 18, color: wz.primary }} />
          <span className={classes.name} title={name || undefined}>
            {name || t('wizJudgTemplatePlaceholder')}
          </span>
          {busy ? <MiSpinner size={16} /> : null}
          {modified ? <span className={classes.badge}>{t('wizJudgModified')}</span> : null}
          <ExpandMoreIcon sx={{ fontSize: 22, color: wz.primary }} />
        </button>
        {open ? (
          <div className={classes.menu} role="listbox">
            {templates === null ? (
              <div className={classes.note}>
                <MiSpinner size={16} /> {t('wizJudgTemplatesLoading')}
              </div>
            ) : error ? (
              <div className={classes.note} style={{ color: wz.redFg }}>
                {t('wizJudgTemplatesError')}
              </div>
            ) : templates.length === 0 ? (
              <div className={classes.note}>{t('wizJudgTemplatesNone')}</div>
            ) : (
              templates.map(tpl => (
                <button
                  key={tpl.id}
                  type="button"
                  className={classes.item}
                  onClick={() => {
                    onPick(tpl);
                    setOpen(false);
                  }}
                >
                  <span className={classes.itemText}>
                    <span className={classes.itemName} title={tpl.name}>
                      {tpl.name}
                    </span>
                    <span className={classes.itemMeta} title={tpl.meta}>
                      {tpl.meta}
                    </span>
                  </span>
                  {tpl.id === selectedId ? (
                    <CheckIcon sx={{ fontSize: 18, color: wz.primary }} />
                  ) : null}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const useStyles = makeStyles()({
  wrap: { position: 'relative', flex: 1, maxWidth: 420 },
  field: {
    background: wz.page,
    borderRadius: 10,
    padding: '8px 8px 8px 12px',
    height: 39,
    boxSizing: 'border-box',
    fontSize: 15,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    fontFamily: 'inherit',
    color: wz.text,
    textAlign: 'left',
    minWidth: 0,
  },
  name: { flex: 1, minWidth: 0, ...ellipsis },
  badge: {
    fontSize: 12,
    color: wz.warnFg,
    background: wz.warnBg,
    borderRadius: 6,
    padding: '2px 6px',
  },
  menu: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    background: '#FFF',
    borderRadius: 10,
    boxShadow: wz.shadowMenu,
    padding: 6,
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    color: wz.text,
    textAlign: 'left',
    '&:hover': { background: wz.page },
  },
  itemText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  itemName: { fontSize: 14, ...ellipsis },
  itemMeta: { fontSize: 12, color: wz.muted, ...ellipsis },
  note: {
    padding: '8px 10px',
    fontSize: 13,
    color: wz.muted,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
});

export default TemplateDropdown;
